import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { UserStartPage } from "../User.types";
import { NavigationMenuItem } from "../../navigationMenuSettings/NavigationMenuSettings.types";
import { isNavigationMenuItemHidden } from "../../navigationMenuSettings/navigationMenuItems";

// A Record so a new start page does not compile until it is tied to the menu item that hides it.
const startPageMenuItems: Record<UserStartPage, NavigationMenuItem> = {
    [UserStartPage.Dashboard]: NavigationMenuItem.DashboardOverview,
    [UserStartPage.Frameworks]: NavigationMenuItem.Frameworks,
    [UserStartPage.ProcessingActivities]: NavigationMenuItem.ProcessingActivities,
    [UserStartPage.Record]: NavigationMenuItem.Record,
    [UserStartPage.Assets]: NavigationMenuItem.Systems,
    [UserStartPage.Risk]: NavigationMenuItem.Risk,
    [UserStartPage.Contracts]: NavigationMenuItem.Contracts,
    [UserStartPage.Tasks]: NavigationMenuItem.Tasks,
    [UserStartPage.AnnualWheelActivity]: NavigationMenuItem.AnnualWheel,
    [UserStartPage.Policies]: NavigationMenuItem.Policies,
    [UserStartPage.IncidentLog]: NavigationMenuItem.IncidentLog,
    [UserStartPage.LegalEntityVendors]: NavigationMenuItem.ExternalLegalEntities,
    [UserStartPage.LegalEntityCustomers]: NavigationMenuItem.ExternalLegalEntities,
    [UserStartPage.LegalEntityGroupCompanies]: NavigationMenuItem.GroupEntities,
    [UserStartPage.LegalEntityOthers]: NavigationMenuItem.ExternalLegalEntities,
    [UserStartPage.ExternalAudits]: NavigationMenuItem.ExternalAudits,
    [UserStartPage.InternalAssessments]: NavigationMenuItem.InternalAssessments,
    [UserStartPage.LegalEntityExternal]: NavigationMenuItem.ExternalLegalEntities,
};

interface StartPageDestination {
    startPages: Array<UserStartPage>;
    path: string;
    hasAccess: boolean;
}

export function useStartPage() {
    const { permissions, startPage, showLegacyRiskAssessments, hiddenNavigationMenuItems } = useUserContext();
    const { platformFeatures } = usePlanContext();

    const isStartPageShownInMenu = (userStartPage: UserStartPage) => !isNavigationMenuItemHidden(hiddenNavigationMenuItems, startPageMenuItems[userStartPage]);

    // In menu order, so a user whose own start page is unavailable lands on the first page their menu offers.
    const destinations: Array<StartPageDestination> = [
        { startPages: [UserStartPage.Dashboard], path: "privacy/dashboard", hasAccess: platformFeatures.dashboard },
        { startPages: [UserStartPage.Frameworks], path: "privacy/frameworks", hasAccess: permissions.frameworkPermissions.read },
        {
            startPages: [UserStartPage.ProcessingActivities],
            path: "privacy/processingactivities",
            hasAccess: permissions.processingActivityPermissions.read,
        },
        { startPages: [UserStartPage.Record], path: "privacy/record", hasAccess: permissions.canAccessRecord301 || permissions.canAccessRecord302 },
        { startPages: [UserStartPage.Assets], path: "privacy/systems", hasAccess: permissions.systemPermissions.read },
        { startPages: [UserStartPage.Risk], path: "privacy/risk-assessments", hasAccess: permissions.riskPermissions.read && showLegacyRiskAssessments },
        {
            startPages: [UserStartPage.Contracts],
            path: "privacy/contracts",
            hasAccess: permissions.contractManagementPermissions.canAccessContractsOverview,
        },
        { startPages: [UserStartPage.Tasks], path: "privacy/task-management", hasAccess: permissions.taskPermissions.read },
        { startPages: [UserStartPage.AnnualWheelActivity], path: "privacy/annual-wheel", hasAccess: permissions.annualWheelPermission.read },
        { startPages: [UserStartPage.Policies], path: "privacy/policies", hasAccess: permissions.policyPermissions.read },
        { startPages: [UserStartPage.IncidentLog], path: "privacy/incident-log", hasAccess: permissions.incidentLogPermissions.read },
        {
            // Vendors, customers and others were merged into the external list, but a user may still have one of the
            // old lists saved as their start page.
            startPages: [
                UserStartPage.LegalEntityExternal,
                UserStartPage.LegalEntityVendors,
                UserStartPage.LegalEntityCustomers,
                UserStartPage.LegalEntityOthers,
            ],
            path: "privacy/external-legal-entities",
            hasAccess: permissions.companyPermissions.read,
        },
        { startPages: [UserStartPage.LegalEntityGroupCompanies], path: "privacy/group-entities", hasAccess: permissions.companyPermissions.read },
        { startPages: [UserStartPage.ExternalAudits], path: "privacy/audit-groups", hasAccess: permissions.canManageAuditGroups },
        { startPages: [UserStartPage.InternalAssessments], path: "privacy/internal-assessments", hasAccess: permissions.canManageInternalAssessment },
    ];

    const availableDestinations = destinations.filter((destination) => destination.hasAccess && isStartPageShownInMenu(destination.startPages[0]));

    function getStartPagePath() {
        const localStorageUserStartPage = localStorage.getItem("UserStartPage");
        const userStartPage = localStorageUserStartPage ? Number(localStorageUserStartPage) : startPage;

        if (!userStartPage) {
            return undefined;
        }

        const ownStartPage = availableDestinations.find((destination) => destination.startPages.includes(userStartPage));
        const fallbackPath = isStartPageShownInMenu(UserStartPage.Dashboard) ? "privacy/dashboard" : undefined;
        return (ownStartPage ?? availableDestinations[0])?.path ?? fallbackPath;
    }

    return { getStartPagePath, isStartPageShownInMenu };
}
